import { buildComputerMenuCatalog } from '#pplx/frontend/apps/ask/ask-input/ask-input-computer-command-menu/computerMenuModel.js';
import { useComputerMenu } from '#pplx/frontend/apps/ask/ask-input/ask-input-computer-command-menu/useComputerMenu.js';
import { useComputerMenuSearchPresentation } from '#pplx/frontend/apps/ask/ask-input/ask-input-computer-command-menu/useComputerMenuSearchPresentation.js';
import { useConnectedConnectors } from '#pplx/frontend/apps/ask/computer-plugins/hooks/useConnectedConnectors.js';
import { usePluginEntitiesEnabled } from '#pplx/frontend/apps/ask/connectors/hooks/usePluginsNavigationHrefs.js';
import { ForkCommandContext } from '#pplx/frontend/apps/ask/providers/BranchModeProviderShared.js';
import { useDebouncedValue } from '#pplx/frontend/common/hooks/useDebouncedValue.js';
import { EVENT_PAYLOAD } from '#pplx/frontend/framework/instrumentation/types.js';
import { useCallback, useContext, useMemo } from 'react';
const computerCommandMenuLoadMoreClickedEvent = EVENT_PAYLOAD;
const COMPUTER_MENU_SEARCH_DEBOUNCE_MS = 250;
const COMPUTER_MENU_SEARCH_MAX_WAIT_MS = COMPUTER_MENU_SEARCH_DEBOUNCE_MS;
function getNormalizedSearchQuery(searchQuery) {
    return searchQuery?.trim().toLocaleLowerCase() ?? '';
}
/**
 * Builds the cloud command-menu catalog and preserves live search presentation
 * while server results are pending, including pagination.
 */
export function useComputerCommandMenuData({ enabled = true, searchQuery, collectionUuid, sideChatAvailable, intl, track, isIncognito, isFollowUp, isAlwaysIncognitoRequired, hideCommandItems, hideSettingsAction, }) {
    const { enabled: pluginsEnabled } = usePluginEntitiesEnabled();
    const connectorsQuery = useConnectedConnectors(enabled && pluginsEnabled);
    const { fetchNextPage: fetchNextConnectorsPage } = connectorsQuery;
    const connectors = useMemo(() => pluginsEnabled ? (connectorsQuery.data?.pages.flatMap((page) => page.sources) ?? []) : [], [connectorsQuery.data, pluginsEnabled]);
    const normalizedSearchQuery = getNormalizedSearchQuery(searchQuery);
    const debouncedSearchQuery = useDebouncedValue(normalizedSearchQuery, COMPUTER_MENU_SEARCH_DEBOUNCE_MS, {
        maxWait: COMPUTER_MENU_SEARCH_MAX_WAIT_MS,
    }) || undefined;
    const resolvedSearchQuery = debouncedSearchQuery ?? '';
    const { featuredItems, commandItems, skills, pluginItems, isLoading, isPlaceholderData = false, isFetchingSearch = false, isFetchingNextPage, hasNextPage, fetchNextPage, } = useComputerMenu({
        enabled,
        collectionUuid,
        sideChatAvailable,
        query: debouncedSearchQuery,
    });
    const forkAvailable = useContext(ForkCommandContext) !== null;
    const catalog = useMemo(() => buildComputerMenuCatalog({
        featuredItems: forkAvailable
            ? featuredItems
            : featuredItems.filter((item) => item.action.type !== 'ui_action' || item.action.action_id !== 'fork_session'),
        commandItems,
        skills,
        plugins: pluginItems,
        connectors,
        intl,
        isIncognito,
        isFollowUp,
        isAlwaysIncognitoRequired,
        hideCommandItems,
        hideSettingsAction,
    }), [
        commandItems,
        connectors,
        featuredItems,
        forkAvailable,
        hideCommandItems,
        hideSettingsAction,
        intl,
        isAlwaysIncognitoRequired,
        isFollowUp,
        isIncognito,
        pluginItems,
        skills,
    ]);
    const presentation = useComputerMenuSearchPresentation({
        catalog,
        search: {
            liveQuery: normalizedSearchQuery,
            resolvedQuery: resolvedSearchQuery,
            isLoading: isLoading || (pluginsEnabled && connectorsQuery.isLoading),
            isPlaceholderData,
            isFetching: isFetchingSearch,
        },
    });
    const showNoCustomSkillsPlaceholder = skills.length === 0 && !normalizedSearchQuery && !isLoading;
    const fetchMore = useCallback(() => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
        if (pluginsEnabled && connectorsQuery.hasNextPage && !connectorsQuery.isFetching) {
            void fetchNextConnectorsPage();
        }
    }, [
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
        pluginsEnabled,
        connectorsQuery.hasNextPage,
        connectorsQuery.isFetching,
        fetchNextConnectorsPage,
    ]);
    const handleLoadMoreClick = useCallback(() => {
        track(computerCommandMenuLoadMoreClickedEvent({
            name: 'computer command menu load more clicked',
            data: {
                has_search_query: resolvedSearchQuery.length > 0,
            },
        }));
        fetchMore();
    }, [fetchMore, resolvedSearchQuery, track]);
    const pagination = useMemo(() => ({
        hasNextPage: hasNextPage || (pluginsEnabled && Boolean(connectorsQuery.hasNextPage)),
        isFetchingNextPage: isFetchingNextPage || (pluginsEnabled && connectorsQuery.isFetchingNextPage),
        onFetchNextPage: fetchMore,
        onLoadMoreClick: handleLoadMoreClick,
    }), [
        fetchMore,
        handleLoadMoreClick,
        hasNextPage,
        isFetchingNextPage,
        pluginsEnabled,
        connectorsQuery.hasNextPage,
        connectorsQuery.isFetchingNextPage,
    ]);
    return {
        resolvedSearchQuery,
        presentation,
        showNoCustomSkillsPlaceholder,
        isLoading: isLoading || (pluginsEnabled && connectorsQuery.isLoading),
        pagination,
    };
}
//# sourceMappingURL=useComputerCommandMenuData.js.map